
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import type { RainfallRow } from '@/pages/customCalculation/model';
import { formatDateTimeToYYYYMMDDHHMM } from '@/functions';
import { useSysInfoStore, useResultAllStore } from '@/shared/service';
import { generateAPI } from '@/shared/lib';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import RainfallTable from './RainfallTable.vue';
import BulkRainfallOperation from './BulkRainfallOperation.vue';

@Component({
  components: {
    RainfallTable,
    BulkRainfallOperation,
    VueCtkDateTimePicker,
  },
})
export default class SettingRainfallConditions extends Vue {
  @Prop({ type: Function, required: true })
  onCalculationExecuted!: () => void;

  @Prop({ type: Function, required: true })
  onSkipToQoutAll!: () => void;

  @Prop({ type: Function, required: true })
  setManualCalculationFileName!: (fileName: string) => void;

  @Prop({ type: String, required: true })
  targetTime!: string;

  @Prop({ type: Function, required: true })
  setTargetTime!: (value: string) => void;

  @Prop({ type: String, required: false, default: '2200-01-01 00:00' })
  latestTargetTime!: string;

  private resultAllStore = useResultAllStore();

  rainfallData: RainfallRow[] = [];

  isCalculating = false;

  pollingIntervalId: number | null = null;

  bugFixFlag = false;

  get dataPickerValue() {
    return this.targetTime;
  }

  set dataPickerValue(value: string) {
    if (this.bugFixFlag) {
      this.setTargetTime(value);
    }
  }

  get basinId() {
    return this['$store'].getters.currentContent.resultKey;
  }

  @Watch('targetTime')
  fetchResultAll() {
    if (!this.targetTime) {
      return;
    }
    const formattedTargetTime = formatDateTimeToYYYYMMDDHHMM(this.targetTime);
    this.resultAllStore.fetch(this.basinId, formattedTargetTime);
  }

  @Watch('resultAllStore.allResults', { deep: true })
  updateRainfallData() {
    this.generateRainfallData();
  }

  setupDataPicker() {
    this.bugFixFlag = false;
    setTimeout(() => {
      this.bugFixFlag = true;
      this.setupDataPickerEvents();
    }, 1000);
  }

  mounted() {
    this.fetchResultAll();
    this.setupDataPicker();
  }

  setupDataPickerEvents() {
    const addEvent = () => {
      const elementValidate = document.getElementsByClassName('datepicker-button validate');
      for (let i = 0; i < elementValidate.length; i += 1) {
        elementValidate[i].removeEventListener('click', this.reflectTargetTime);
        elementValidate[i].addEventListener('click', this.reflectTargetTime);
      }
    };
    addEvent();

    const datepickerElement = document.getElementsByClassName('datepicker flex flex-direction-column').item(0) as HTMLElement;
    if (datepickerElement) {
      datepickerElement.style.height = '500px';
    }
  }

  reflectTargetTime() {
    // GraphSetting.vueと同様、validateボタンクリック時の処理
    // dataPickerValueのsetterが呼ばれるため、特別な処理は不要
  }

  generateRainfallData() {
    const { allResults } = this.resultAllStore;
    if (!allResults || !allResults.stations || allResults.stations.length === 0) {
      this.rainfallData = [];
      return;
    }

    const sysInfoStore = useSysInfoStore();
    const { stationsMap } = sysInfoStore;

    // すべての観測所からユニークな時間を取得
    const datetimeSet = new Set<string>();
    allResults.stations.forEach(station => {
      station.simResult.forEach(data => {
        datetimeSet.add(data.datetime);
      });
    });

    // 時間でソート
    const sortedDatetimes = Array.from(datetimeSet).sort();

    // 6時間前のデータをスキップ（6時間 × 60分 ÷ 10分 = 36個）
    // 10分刻みなので1時間に6個のデータがある
    // インデックス0が6時間前、インデックス36が現時刻（0時間目）
    const skipCount = 36;
    const filteredDatetimes = sortedDatetimes.slice(skipCount);

    // 1時間単位にフィルタリング（6個に1個を抽出）
    const hourlyDatetimes = filteredDatetimes.filter((_, index) => index % 6 === 0);

    // 時間ごとにRainfallRowを作成
    this.rainfallData = hourlyDatetimes.map((datetime, index) => {
      const dams = allResults.stations.map(station => {
        const simData = station.simResult.find(d => d.datetime === datetime);
        const damName = stationsMap?.[station.id]?.name ?? station.id;
        return {
          id: station.id,
          label: `${damName}雨量 [mm/h]`,
          before: simData?.r ?? 0,
          after: simData?.r ?? 0,
        };
      });

      return {
        label: index === 0 ? '現時刻' : `${index}時間後`,
        dateTime: datetime,
        dams,
      };
    });
  }

  onOperationUpdated() {
    // 更新を検出するため、再レンダリングをトリガー
    this.$forceUpdate();
  }

  /**
   * 降雨データをAPI送信用フォーマットに変換
   */
  formatApiData() {
    const formattedTime = formatDateTimeToYYYYMMDDHHMM(this.targetTime);
    const year = formattedTime.substring(0, 4);
    const month = formattedTime.substring(4, 6);
    const day = formattedTime.substring(6, 8);
    
    const targetFile = `results/${this.basinId}/${year}/${month}/${day}/${formattedTime}.zip`;
    
    const apiData: { [key: string]: any } = {
      targetFile,
    };

    // 各観測所のデータを作成
    if (this.rainfallData.length > 0) {
      this.rainfallData[0].dams.forEach(dam => {
        const stationId = dam.id;
        const rainValues: number[] = [];
        
        // 各時間の降雨量を取得（after値を使用）
        this.rainfallData.forEach(row => {
          const damData = row.dams.find(d => d.id === stationId);
          if (damData) {
            const value = typeof damData.after === 'string' ? parseFloat(damData.after) : damData.after;
            rainValues.push(value || 0);
          }
        });

        apiData[stationId] = {
          rain: rainValues,
        };
      });
    }

    return apiData;
  }

  /**
   * 計算実行ボタンのクリックハンドラ
   */
  async executeCalculation() {
    if (this.isCalculating) {
      return;
    }

    try {
      this.isCalculating = true;
      
      const apiData = this.formatApiData();
      
      const response = await generateAPI().customCalcApi.saveCustomCalcConditionApiApiCustomCalcConditionPost({ body: apiData });

      // 既存のポーリングがあれば停止
      this.stopPolling();

      // 結果をポーリング
      this.startPolling(response.filename);
      
    } catch (error) {
      console.error('計算実行エラー:', error);
      this.isCalculating = false;
      // TODO: エラーダイアログ表示
    }
  }

  /**
   * ポーリングを開始
   */
  startPolling(filename: string) {
    this.pollingIntervalId = window.setInterval(async () => {
      try {
        const result = await generateAPI().customCalcApi.checkCustomCalcResultExistsApiApiCustomCalcExistsBasinIdGet({
          basinId: this.basinId,
          filename,
        });
        
        if (result.fileExists) {
          this.stopPolling();
          this.isCalculating = false;
          this.setManualCalculationFileName(filename);
          this.onCalculationExecuted();
        }
      } catch (error) {
        console.error('ポーリングエラー:', error);
        this.stopPolling();
        this.isCalculating = false;
      }
    }, 10000);
  }

  /**
   * ポーリングを停止
   */
  stopPolling() {
    if (this.pollingIntervalId !== null) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  /**
   * コンポーネント破棄時にポーリングをクリーンアップ
   */
  beforeDestroy() {
    this.stopPolling();
  }
}
